import React from "react";
import { toast } from "react-toastify";
import { Page, Table, Button, Typography, Input } from "../../../../design-system";
import Modal from "../../../../design-system/molecules/Modal";
import LoadingContainer from "../../../../design-system/molecules/LoadingContainer";
import { createSupplier, getSuppliers, updateSupplier, ToogleActivationSupplier } from "../../../../api/suppliers";

const initialSupplierData = {
    name: '',
    email: '',
    phone_number: ''
};

export default props => {
    const [data, setData] = React.useState([]);
    const [loading, setLoading] = React.useState(false);

    // States for Create Modal
    const [supplierData, setSupplierData] = React.useState(initialSupplierData);
    const [showCreate, setShowCreate] = React.useState(false);
    const [isCreating, setIsCreating] = React.useState(false);

    // States for Edit Modal
    const [currentSupplier, setCurrentSupplier] = React.useState(initialSupplierData);
    const [showEdit, setShowEdit] = React.useState(false);
    const [isEditing, setIsEditing] = React.useState(false);

    async function loadData(params = { is_active: true }) {
        setLoading(true);
        try {
            const result = await getSuppliers(params);
            setData(result.reverse());
        } catch (error) {
            toast.error("Erro ao carregar fornecedores.");
        } finally {
            setLoading(false);
        }
    }

    React.useEffect(() => {
        loadData();
    }, []);

    const handleCreateChange = (e) => {
        const { name, value } = e.target;
        setSupplierData(prev => ({ ...prev, [name]: value }));
    };

    const handleEditChange = (e) => {
        const { name, value } = e.target;
        setCurrentSupplier(prev => ({ ...prev, [name]: value }));
    };

    const openCreateModal = () => {
        setSupplierData(initialSupplierData);
        setShowCreate(true);
    };

    const closeCreateModal = () => {
        setShowCreate(false);
    };

    const openEditModal = (supplier) => {
        setCurrentSupplier(supplier);
        setShowEdit(true);
    };

    const closeEditModal = () => {
        setShowEdit(false);
    };

    const handleCreateSupplier = async () => {
        setIsCreating(true);
        try {
            await createSupplier(supplierData);
            toast.success("Fornecedor cadastrado com sucesso!", { position: "top-center" });
            closeCreateModal();
            loadData();
        } catch (error) {
            toast.error("Erro ao cadastrar fornecedor.", { position: "top-center" });
        } finally {
            setIsCreating(false);
        }
    };

    const handleUpdateSupplier = async () => {
        setIsEditing(true);
        try {
            await updateSupplier(currentSupplier.id, currentSupplier);
            toast.success("Fornecedor editado com sucesso!", { position: "top-center" });
            closeEditModal();
            loadData();
        } catch (error) {
            toast.error("Erro ao editar fornecedor.", { position: "top-center" });
        } finally {
            setIsEditing(false);
        }
    };

    const handleToggleActivation = async (id) => {
        try {
            await ToogleActivationSupplier(id);
            toast.success("Status do fornecedor alterado com sucesso!", { position: "top-center" });
            loadData();
        } catch (error) {
            toast.error("Erro ao alterar status do fornecedor.", { position: "top-center" });
        }
    };

    return (
        <Page
            title="Fornecedores"
            subtitle="Gerencie seus fornecedores"
            actions={
                <Button onClick={openCreateModal} variant="primary">
                    <i className="fas fa-plus mr-2" /> Cadastrar Fornecedor
                </Button>
            }
        >
            <LoadingContainer loading={loading}>
                <Table striped hover responsive paginate searchable searchPlaceholder="Buscar fornecedores...">
                    <Table.Header>
                        <Table.Row>
                            <Table.HeaderCell>Nome</Table.HeaderCell>
                            <Table.HeaderCell>E-mail</Table.HeaderCell>
                            <Table.HeaderCell>Telefone</Table.HeaderCell>
                            <Table.HeaderCell style={{ textAlign: 'center', width: '150px' }}>Ações</Table.HeaderCell>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {data.length === 0 && !loading ? (
                            <Table.Row>
                                <Table.Cell colSpan={4} style={{ textAlign: 'center', padding: '2rem' }}>
                                    <Typography variant="paragraph" style={{ fontStyle: 'italic' }}>
                                        Nenhum fornecedor encontrado.
                                    </Typography>
                                </Table.Cell>
                            </Table.Row>
                        ) : (
                            data.map((item) => (
                                <Table.Row key={item.id}>
                                    <Table.Cell>{item.name}</Table.Cell>
                                    <Table.Cell>{item.email}</Table.Cell>
                                    <Table.Cell>{item.phone_number}</Table.Cell>
                                    <Table.Cell style={{ textAlign: 'center' }}>
                                        <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem' }}>
                                            <Button
                                                variant="tertiary"
                                                size="xs"
                                                onClick={() => openEditModal(item)}
                                                title="Editar Fornecedor"
                                            >
                                                <i className="fa fa-edit" />
                                            </Button>
                                            <Button
                                                variant="tertiary"
                                                size="xs"
                                                onClick={() => {
                                                    if (window.confirm("Deseja realmente desativar este fornecedor?")) {
                                                        handleToggleActivation(item.id);
                                                    }
                                                }}
                                                title="Desativar Fornecedor"
                                                style={{ color: '#dc3545' }}
                                            >
                                                <i className="fa fa-ban" />
                                            </Button>
                                        </div>
                                    </Table.Cell>
                                </Table.Row>
                            ))
                        )}
                    </Table.Body>
                </Table>
            </LoadingContainer>

            {/* Create Supplier Modal */}
            <Modal
                isOpen={showCreate}
                onClose={closeCreateModal}
                title="Cadastrar Fornecedor"
                primaryAction={{
                    label: 'Cadastrar',
                    onClick: handleCreateSupplier,
                    disabled: !supplierData.name || isCreating
                }}
                secondaryAction={{
                    label: 'Cancelar',
                    onClick: closeCreateModal,
                    disabled: isCreating
                }}
                loading={isCreating}
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <Input
                        label="Nome"
                        name="name"
                        placeholder="Insira o nome do fornecedor"
                        value={supplierData.name}
                        onChange={handleCreateChange}
                        disabled={isCreating}
                    />
                    <Input
                        label="E-mail"
                        name="email"
                        type="email"
                        placeholder="Insira o e-mail"
                        value={supplierData.email}
                        onChange={handleCreateChange}
                        disabled={isCreating}
                    />
                    <Input
                        label="Telefone"
                        name="phone_number"
                        type="text"
                        placeholder="Insira o telefone"
                        value={supplierData.phone_number}
                        onChange={handleCreateChange}
                        disabled={isCreating}
                        maxLength={15}
                    />
                </div>
            </Modal>

            {/* Edit Supplier Modal */}
            <Modal
                isOpen={showEdit}
                onClose={closeEditModal}
                title={`Editar ${currentSupplier.name}`}
                primaryAction={{
                    label: 'Salvar Alterações',
                    onClick: handleUpdateSupplier,
                    disabled: !currentSupplier.name || isEditing
                }}
                secondaryAction={{
                    label: 'Cancelar',
                    onClick: closeEditModal,
                    disabled: isEditing
                }}
                loading={isEditing}
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <Input
                        label="Novo Nome"
                        name="name"
                        placeholder="Insira o novo nome"
                        value={currentSupplier.name}
                        onChange={handleEditChange}
                        disabled={isEditing}
                    />
                    <Input
                        label="Novo E-mail"
                        name="email"
                        type="email"
                        placeholder="Insira o novo e-mail"
                        value={currentSupplier.email}
                        onChange={handleEditChange}
                        disabled={isEditing}
                    />
                    <Input
                        label="Novo Telefone"
                        name="phone_number"
                        type="text"
                        placeholder="Insira o novo telefone"
                        value={currentSupplier.phone_number}
                        onChange={handleEditChange}
                        disabled={isEditing}
                        maxLength={15}
                    />
                </div>
            </Modal>
        </Page>
    );
};



// WEBPACK FOOTER //
// ./src/views/AppViews/Products/Suppliers/index.jsx